import { LICENSE_MODULE } from "@selector/shared";
import { selMessage } from "@selector/shared";
import { uniq } from "ramda";
import { Component, type ReactElement } from "react";
import { connect } from "react-redux";
import { destroy, formValueSelector } from "redux-form";

import { buildHelpLink } from "@/app-utils/help.tsx";
import {
  getIconElementForCustomPlaybook,
  type ICombinedRegistrationNgsPlaybook,
  type ICombinedRegistrationNgsPlaybookConfig,
  type IConfiguredPlaybook,
  type IPlaybookPage,
  isNgsPlaybook,
} from "@/app-utils/playbook.ts";
import { getPrimaryParentType } from "@/app-utils/sample.ts";
import { type Attribute, type Configuration, type ParentType, type User, type Workflow } from "@/graphql.ts";
import { isLicensed } from "@/redux/modules/config.ts";
import {
  clearPlaybookExecutionData,
  initPlaybookExecutionState,
  initPlaybookExecutionStateFromDemoData,
  type IPlaybookExecutionState,
  isNgsDataFileAttributeByUsage,
  isNgsDataFileWorkflowParam,
  loadPlaybook,
  loadWorkflow,
  submitPlaybook,
} from "@/redux/modules/playbook-execution.ts";
import {
  clearSampleEditingData,
  type ISampleEditingState,
  loadSampleTypeDataForPlaybook,
} from "@/redux/modules/sample-editing.ts";
import { type IAppState } from "@/redux/reducer.ts";
import {
  PLAYBOOK_TYPE,
  SAMPLE_PLAYBOOK_EXECUTION_SCREENS,
  SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS,
  SAMPLE_TRACKING,
} from "@/settings/settings.ts";

import PlaybookWizard from "../PlaybookWizard.js";
import { type IWizardStep } from "../PlaybookWizardStep.js";
import CombinedRegistrationNgsRawDataScreen from "./CombinedRegistrationNgsAnalysisDataScreen.js";
import {
  CombinedRegistrationNgsBasicsScreen,
  CombinedRegistrationNgsBasicsScreenImpl,
} from "./CombinedRegistrationNgsBasicsScreen.js";
import CombinedRegistrationNgsSelectSampleScreen from "./CombinedRegistrationNgsExistingSampleGridScreen.js";
import CombinedRegistrationNgsGridScreen from "./CombinedRegistrationNgsNewSampleGridScreen.js";
import { CombinedRegistrationNgsRangeScreen } from "./CombinedRegistrationNgsRangeScreen.js";
import CombinedRegistrationNgsReviewScreen from "./CombinedRegistrationNgsReviewScreen.js";

interface ICombinedSampleRegistrationNgsWizardOwnProps {
  onClose: () => void;
}

interface ICombinedSampleRegistrationNgsWizardAppStateProps {
  selectedPlaybookId: number;
  playbookExecution: IPlaybookExecutionState;
  documentationUrl: string;
  canExecuteNGSAnalysis: boolean;
  selectedSampleTracking: SAMPLE_TRACKING;
  sampleEditingState: ISampleEditingState;
}

interface ICombinedSampleRegistrationNgsWizardDispatchProps {
  initPlaybookState: () => void;
  clearSampleEditingData: () => void;
  submitPlaybook: () => Promise<void>;
  loadFullPlaybook: (id: number) => Promise<ICombinedRegistrationNgsPlaybook>;
  loadSampleTypeData: (sampleTypeId: number, mandatoryAttributeIds: number[]) => Promise<Partial<ISampleEditingState>>;
  loadWorkflow: (playbook: ICombinedRegistrationNgsPlaybook) => Promise<Workflow>;
  initializeWithDemoData: () => void;
}

export interface ICombinedSampleRegistrationNgsWizardProps
  extends ICombinedSampleRegistrationNgsWizardAppStateProps,
    ICombinedSampleRegistrationNgsWizardDispatchProps,
    ICombinedSampleRegistrationNgsWizardOwnProps {}

function mapStateToProps(
  state: IAppState,
  _ownProps: ICombinedSampleRegistrationNgsWizardProps,
): ICombinedSampleRegistrationNgsWizardAppStateProps {
  return {
    sampleEditingState: state.sampleEditingState,
    selectedPlaybookId: state.playbookExecution.selectedPlaybookId,
    playbookExecution: state.playbookExecution,
    documentationUrl: state.config.configuration?.application?.documentationUrl,
    canExecuteNGSAnalysis: CombinedSampleRegistrationNgsWizardImpl.canLaunchProcessor(
      state.config.configuration,
      state.auth.session,
    ),
    selectedSampleTracking:
      formValueSelector(CombinedRegistrationNgsBasicsScreenImpl.COMBINED_REGISTRATION_NGS_PARENT_SCREEN)(
        state,
        CombinedRegistrationNgsBasicsScreenImpl.FIELD_SAMPLE_TRACKING,
      )?.value || SAMPLE_TRACKING.USE_EXISTING,
  };
}

function mapDispatchToProps(dispatch): ICombinedSampleRegistrationNgsWizardDispatchProps {
  return {
    initPlaybookState: () => dispatch(initPlaybookExecutionState()),
    clearSampleEditingData: () => {
      dispatch(clearSampleEditingData());
      dispatch(clearPlaybookExecutionData());
      // clear the involved forms (done by default anyway unless specified in the redux-form component
      // with {destroyOnUnmount: false) so that we can go back and forth in the wizard without registration process in between)
      dispatch(destroy(CombinedRegistrationNgsBasicsScreen.COMBINED_REGISTRATION_NGS_PARENT_SCREEN));
    },
    submitPlaybook: () => dispatch(submitPlaybook()),
    loadFullPlaybook: (id: number) => dispatch(loadPlaybook(id)),
    loadSampleTypeData: (sampleTypeId: number, mandatoryAttributeIds: number[]) =>
      dispatch(loadSampleTypeDataForPlaybook(sampleTypeId, mandatoryAttributeIds)),
    loadWorkflow: (playbook: ICombinedRegistrationNgsPlaybook) => dispatch(loadWorkflow(playbook)),
    initializeWithDemoData: () => dispatch(initPlaybookExecutionStateFromDemoData()),
  };
}

interface ICombinedSampleRegistrationNgsWizardState {
  isLoading: boolean; // fetch the Playbook itself
  isInitializing: boolean; // fetch initial data that the Playbook depends on
  configurationError: any;
}

/**
 * Wizard of Playbook Type "Sample Registration with NGS Analysis"
 *
 * created on 1/31/2018
 */
class CombinedSampleRegistrationNgsWizardImpl extends Component<
  ICombinedSampleRegistrationNgsWizardProps,
  ICombinedSampleRegistrationNgsWizardState
> {
  constructor(props: ICombinedSampleRegistrationNgsWizardProps) {
    super(props);

    this.state = {
      isLoading: true,
      isInitializing: true,
      configurationError: null,
    };
  }

  public static canLaunchProcessor = (configuration: Configuration, user: User): boolean => {
    return isLicensed(configuration, LICENSE_MODULE.PROCESSOR);
  };

  verifyPlaybookConfiguration = (playbook: IConfiguredPlaybook) => {
    if (playbook.type.id === PLAYBOOK_TYPE.NGS_ANALYSIS_WITH_REGISTERED_SAMPLES) {
      if (isNgsPlaybook(playbook) && !playbook.config.ngsWorkflow?.workflowImportMappings?.length) {
        throw new Error(selMessage({ id: "playbook.execution.error.missingFileAttribute" }).toString());
      }
    }
  };

  componentDidMount() {
    this.props
      .loadFullPlaybook(this.props.selectedPlaybookId)
      .then((playbook) => {
        this.setState({ isLoading: false });
        this.verifyPlaybookConfiguration(playbook);
        return playbook;
      })
      .then((playbook: ICombinedRegistrationNgsPlaybook) => {
        if (playbook.type.id !== PLAYBOOK_TYPE.NGS_ANALYSIS && playbook.type.id !== PLAYBOOK_TYPE.RANGE_ANALYSIS) {
          return this.loadRelatedSampleType(playbook);
        }
        return playbook;
      })
      .then((playbook: ICombinedRegistrationNgsPlaybook) => {
        if (
          playbook.type.id === PLAYBOOK_TYPE.NGS_ANALYSIS_WITH_REGISTERED_SAMPLES ||
          playbook.type.id === PLAYBOOK_TYPE.NGS_ANALYSIS ||
          playbook.type.id === PLAYBOOK_TYPE.RANGE_ANALYSIS
        ) {
          return this.initializeWorkflowInputs(playbook);
        }
      })
      .then(() => this.props.initPlaybookState())
      .then(() => this.setState({ isInitializing: false }))
      .catch((error: Error) => {
        this.updateConfigurationError(error.message);
      });
  }

  onClose = () => {
    this.props.onClose();
    // clear sample-registration data
    this.props.clearSampleEditingData();
  };

  loadRelatedSampleType = (playbook: ICombinedRegistrationNgsPlaybook) => {
    // attributes that are mapped to workflow params by configuration
    // and fields that were set to mandatory on playbook level are mandatory

    const mandatoryAttributeIds: number[] = [];

    mandatoryAttributeIds.push(
      ...(playbook.config.ngsWorkflow?.workflowImportMappings?.map((mapping) => mapping.attributeId) || []),
    );

    mandatoryAttributeIds.push(
      ...(playbook.config.sampleInput.filter((i) => i.correctedMandatory).map((i) => i.trackerAttributeId) || []),
    );

    return this.props
      .loadSampleTypeData(playbook.sampleType.id, uniq(mandatoryAttributeIds))
      .then((response: Partial<ISampleEditingState>) => {
        const primaryParentType: ParentType = getPrimaryParentType(response.sampleType);

        if (!primaryParentType) {
          throw new Error(
            selMessage({
              id: "playbook.execution.error.missingPrimaryParent",
              values: { sampleTypeName: playbook.sampleType.name },
            }).toString(),
          );
        }
        return playbook;
      });
  };

  initializeWorkflowInputs = (playbook: ICombinedRegistrationNgsPlaybook) => {
    const playbookConfig: ICombinedRegistrationNgsPlaybookConfig = playbook.config;

    if (!this.props.canExecuteNGSAnalysis) {
      throw new Error(
        selMessage({
          id: "playbook.execution.error.notAuthorized",
          values: { sampleTypeName: playbook.sampleType.name },
        }).toString(),
      );
    }
    return this.props
      .loadWorkflow(playbook)
      .catch((error: Error) => {
        throw new Error(error.message);
      })
      .then((selectedWorkflow: Workflow) => {
        if (!selectedWorkflow) {
          throw new Error(
            selMessage({
              id: "playbook.execution.error.workflowNotFound",
              values: { workflowId: playbookConfig.ngsWorkflow.workflowId },
            }).toString(),
          );
        }
        if (!selectedWorkflow.isValid) {
          throw new Error(
            selMessage({
              id: "playbook.execution.error.invalidWorkflow",
              values: { workflowName: selectedWorkflow.name },
            }).toString(),
          );
        }
        // check if the underlying workflow was modified after the playbook was configured
        const playbookModifiedDate = new Date(playbook.modified || playbook.created);
        const workflowApprovalDate = new Date(selectedWorkflow.approvalDate);
        if (workflowApprovalDate > playbookModifiedDate) {
          throw new Error(
            selMessage({
              id: "playbook.execution.error.deprecatedApproval",
              values: { workflowName: selectedWorkflow.name },
            }).toString(),
          );
        }
        if (
          playbook.type.id === PLAYBOOK_TYPE.NGS_ANALYSIS_WITH_REGISTERED_SAMPLES ||
          playbook.type.id === PLAYBOOK_TYPE.NGS_ANALYSIS
        ) {
          if (!selectedWorkflow.workflowParams.find(isNgsDataFileWorkflowParam)) {
            throw new Error(
              selMessage({
                id: "playbook.execution.error.missingInputFilesParam",
                values: { workflowName: selectedWorkflow.name },
              }).toString(),
            );
          }
        }
        return selectedWorkflow;
      });
  };

  render(): ReactElement {
    const playbook: IConfiguredPlaybook = this.props.playbookExecution?.playbook;

    const steps: SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS[] = [];
    switch (playbook?.type.id) {
      case PLAYBOOK_TYPE.SAMPLE_REGISTRATION:
        steps.push(
          SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS.PARENT_SCREEN,
          SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS.GRID_SCREEN,
          SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS.REVIEW_SCREEN,
        );
        break;
      case PLAYBOOK_TYPE.NGS_ANALYSIS_WITH_REGISTERED_SAMPLES:
        steps.push(SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS.PARENT_SCREEN);

        if (this.props.selectedSampleTracking === SAMPLE_TRACKING.REGISTER) {
          steps.push(SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS.GRID_SCREEN);
        } else {
          steps.push(SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS.SELECT_SAMPLE_SCREEN);
        }
        // is there workflow params inputs available ?
        const workflowParams: Attribute[] = this.props.playbookExecution.workflowAttributes.filter(
          (attribute: Attribute) => !isNgsDataFileAttributeByUsage(attribute),
        );

        if (workflowParams.length) {
          steps.push(SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS.NGS_DATA_SCREEN);
        }
        steps.push(SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS.REVIEW_SCREEN);
        break;
      case PLAYBOOK_TYPE.NGS_ANALYSIS:
        steps.push(
          SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS.PARENT_SCREEN,
          SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS.NGS_DATA_SCREEN,
          SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS.REVIEW_SCREEN,
        );
        break;
      case PLAYBOOK_TYPE.RANGE_ANALYSIS:
        steps.push(SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS.RANGE_SCREEN);
        break;
    }

    return (
      <PlaybookWizard
        title={playbook?.name}
        suffix={playbook?.nameSuffix}
        isLoading={this.state.isLoading}
        isInitializing={this.state.isInitializing}
        helpLink={this.getHelpLink()}
        className={"sample-registration-wizard"}
        steps={steps.map((step: SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS) => SAMPLE_PLAYBOOK_EXECUTION_SCREENS[step])}
        mapStepToScreen={this.mapStepToScreen}
        needsWideSpace={this.needsWideSpace}
        onClose={this.onClose}
        playbook={true}
        configurationError={this.state.configurationError}
        updateConfigurationError={this.updateConfigurationError}
        icon={getIconElementForCustomPlaybook(playbook)}
        onUseDemoData={this.hasDemoData() && this.props.initializeWithDemoData}
        disableUseDemoDataButton={this.props.playbookExecution?.startedFromSelectedSamples}
      />
    );
  }

  hasDemoData = () => {
    const playbook = this.props.playbookExecution.playbook;
    const workflow = this.props.playbookExecution.workflow;
    if ((!!playbook?.templateEvent || workflow?.isDemo) && playbook?.type.id !== PLAYBOOK_TYPE.RANGE_ANALYSIS) {
      return true;
    }
  };

  getHelpLink = () => {
    let defaultHelpPage: string = null;
    switch (this.props.playbookExecution.playbook?.type.id) {
      case PLAYBOOK_TYPE.SAMPLE_REGISTRATION:
        defaultHelpPage = "Sample%2bRegistration%2bPlaybook";
        break;
      case PLAYBOOK_TYPE.NGS_ANALYSIS_WITH_REGISTERED_SAMPLES:
        defaultHelpPage = "Analysis%2bPlaybook%2bwith%2bSamples";
        break;
      case PLAYBOOK_TYPE.NGS_ANALYSIS:
        defaultHelpPage = "Analysis%2bPlaybook%2bwithout%2bSamples";
        break;
      case PLAYBOOK_TYPE.RANGE_ANALYSIS:
        defaultHelpPage = "Range%2bAnalysis%2bPlaybook";
        break;
      default:
        defaultHelpPage = "";
    }
    return buildHelpLink(
      // standard playbooks come with specific help pages (named after the playbook),
      // while all other playbooks use a more generic help link (based on the playbook type)
      !this.props.playbookExecution.playbook?.standard
        ? defaultHelpPage
        : // encode properly first, then replace the encoded blanks by plus signs for Confluence
          encodeURIComponent(
            `${this.props.playbookExecution?.playbook?.name} Workflow${
              this.props.playbookExecution?.playbook?.nameSuffix
                ? ` ${this.props.playbookExecution?.playbook?.nameSuffix}`
                : ``
            }`,
          ).replace(/%20/g, "%2b"),
      this.props.documentationUrl,
    );
  };

  updateConfigurationError = (error) => this.setState({ configurationError: error });

  mapStepToScreen = (page: IPlaybookPage): IWizardStep => {
    switch (page.pageIdentifier) {
      case SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS.PARENT_SCREEN:
        return <CombinedRegistrationNgsBasicsScreen playbookPage={page} />;
      case SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS.NGS_DATA_SCREEN:
        return <CombinedRegistrationNgsRawDataScreen playbookPage={page} />;
      case SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS.SELECT_SAMPLE_SCREEN:
        return <CombinedRegistrationNgsSelectSampleScreen playbookPage={page} />;
      case SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS.GRID_SCREEN:
        return <CombinedRegistrationNgsGridScreen playbookPage={page} />;
      case SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS.REVIEW_SCREEN:
        return <CombinedRegistrationNgsReviewScreen playbookPage={page} onSubmit={this.props.submitPlaybook} />;
      case SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS.RANGE_SCREEN:
        return <CombinedRegistrationNgsRangeScreen playbookPage={page} onSubmit={this.props.submitPlaybook} />;
    }
  };

  needsWideSpace = (page: IPlaybookPage): boolean => {
    switch (page.pageIdentifier) {
      case SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS.GRID_SCREEN:
      case SAMPLE_REGISTRATION_PLAYBOOK_SCREEN_IDS.SELECT_SAMPLE_SCREEN:
        return true;
      default:
        return false;
    }
  };
}

const CombinedSampleRegistrationNgsWizard = connect<
  ICombinedSampleRegistrationNgsWizardAppStateProps,
  ICombinedSampleRegistrationNgsWizardDispatchProps,
  ICombinedSampleRegistrationNgsWizardOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(CombinedSampleRegistrationNgsWizardImpl);

export { CombinedSampleRegistrationNgsWizard };
export default CombinedSampleRegistrationNgsWizard;
